import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import OpenAIStatusAlert from '../../../components/ui/OpenAIStatusAlert';
import { generateSalesInsights, getOpenAIStatus } from '../../../services/openaiService';
import { useAuth } from '../../../contexts/AuthContext';
import { salesMetricsService } from '../../../services/salesHubService';
import AIStatusDiagnostic from '../../../components/ui/AIStatusDiagnostic';


const AIInsightsPanel = ({ organizationId, refreshTrigger }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiStatus, setAiStatus] = useState('checking');
  const [showApiError, setShowApiError] = useState(false);
  const [diagnosticMode, setDiagnosticMode] = useState(false);

  // Use actual organization ID from context or props
  const orgId = organizationId || userProfile?.organization_id;

  useEffect(() => {
    if (orgId && isAuthenticated) {
      loadSalesInsights();
    }
  }, [orgId, isAuthenticated, refreshTrigger]);

  const loadSalesInsights = async () => {
    if (!orgId || !isAuthenticated) return;

    setLoading(true);
    setError(null);
    setAiStatus('checking');
    setShowApiError(false);
    
    try {
      // Enhanced OpenAI status check with client validation
      const openAIStatus = getOpenAIStatus();
      console.log('OpenAI Status Check:', openAIStatus);
      
      // More detailed API configuration checking
      if (!openAIStatus?.configured || !openAIStatus?.clientStatus?.initialized) {
        const errorDetails = openAIStatus?.clientStatus?.error || openAIStatus?.apiKeyValidation;
        
        setAiStatus('invalid_key');
        setShowApiError(true);
        setError(`OpenAI API configuration issue: ${errorDetails?.message || 'Invalid or missing API key'}`);
        
        // Still provide fallback insights
        const fallbackData = { totalSales: 0, conversionRate: 0, averagePaymentTime: 18, overdueAmount: 0 };
        setInsights(generateDataDrivenInsights(fallbackData, []));
        return;
      }
      
      // Get real sales data from Supabase
      const metricsResult = await salesMetricsService?.getSalesMetrics(orgId);
      
      let salesData = {
        totalSales: 0,
        averageOrderValue: 0,
        conversionRate: 0,
        customerRetention: 0.78,
        topProducts: ['Service A', 'Service B', 'Service C'],
        salesGrowth: 0.12,
        pendingQuotes: 0,
        overdueInvoices: 0
      };

      // Use actual metrics if available
      if (metricsResult?.data) {
        const data = metricsResult?.data;
        salesData = {
          ...salesData,
          totalSales: data?.totalOutstanding || 0,
          conversionRate: data?.conversionRate || 0,
          averagePaymentTime: data?.avgPaymentTime || 18,
          overdueAmount: data?.overdueAmount || 0
        };
      }

      // Generate AI insights based on real data
      const result = await generateSalesInsights(salesData);
      
      // Enhanced error detection
      const isAIUnavailable = result?.[0]?.category === 'system';
      const hasApiError = result?.[0]?.errorDetails;
      
      if (isAIUnavailable) {
        setAiStatus('unavailable');
        
        // Check for specific API key errors with enhanced detection
        if (hasApiError) {
          const errorType = hasApiError?.type;
          const errorContext = hasApiError?.context;
          
          if (['invalid_key', 'no_api_key', 'placeholder_key', 'missing_key', 'corrupted_key']?.includes(errorType)) {
            setAiStatus('invalid_key');
            setShowApiError(true);
            setError(`API Key Issue: ${errorContext?.message || 'Invalid or expired OpenAI API key detected'}`);
          } else if (['quota_exceeded', 'insufficient_quota']?.includes(errorType)) {
            setAiStatus('quota_exceeded');
            setShowApiError(true);
            setError(`Quota Exceeded: ${errorContext?.message || 'OpenAI usage quota has been exceeded'}`);
          } else if (errorType === 'insufficient_permissions') {
            setAiStatus('insufficient_permissions');
            setShowApiError(true);
            setError(`Permissions Issue: ${errorContext?.message || 'API key lacks necessary permissions'}`);
          } else {
            setError('AI sales insights temporarily unavailable - using data-driven analysis');
          }
        } else {
          setError('AI sales insights temporarily unavailable - using data-driven analysis');
        }
      } else {
        setAiStatus('available');
        setShowApiError(false);
      }
      
      // Generate insights based on actual data
      const dynamicInsights = generateDataDrivenInsights(salesData, result);
      setInsights(dynamicInsights);

    } catch (error) {
      console.error('Error generating sales insights:', error);
      
      // Enhanced error handling with specific API error detection
      if (error?.status === 401 || error?.message?.includes('Incorrect API key') || error?.code === 'invalid_api_key') {
        setAiStatus('invalid_key');
        setShowApiError(true);
        setError('Sales analysis failed - invalid or expired API key. Please check your OpenAI configuration.');
      } else if (error?.status === 429 || error?.code === 'insufficient_quota') {
        setAiStatus('quota_exceeded');
        setShowApiError(true);
        setError('Sales analysis failed - OpenAI quota exceeded. Please upgrade your plan for continued access.');
      } else if (error?.status === 403) {
        setAiStatus('insufficient_permissions');
        setShowApiError(true);
        setError('Sales analysis failed - insufficient API permissions.');
      } else if (error?.message?.includes('fetch')) {
        setAiStatus('network_error');
        setError('Network error - unable to connect to OpenAI services.');
      } else {
        setAiStatus('unavailable');
        setError('AI service temporarily unavailable - using data-driven analysis.');
      }
      
      // Fallback to data-driven insights even without AI
      const fallbackData = { totalSales: 0, conversionRate: 0, averagePaymentTime: 18, overdueAmount: 0 };
      setInsights(generateDataDrivenInsights(fallbackData, []));
    } finally {
      setLoading(false);
    }
  };

  // Generate insights based on actual data patterns
  const generateDataDrivenInsights = (salesData, aiInsights) => {
    const insights = [];

    // Use AI insights if available
    if (aiInsights?.length > 0 && aiStatus === 'available') {
      return aiInsights;
    }

    // Generate data-driven insights based on actual metrics
    if (salesData?.conversionRate > 0) {
      if (salesData?.conversionRate < 30) {
        insights?.push({
          type: 'opportunity',
          category: 'conversion',
          title: 'Quote Conversion Opportunity',
          description: `Current conversion rate is ${salesData?.conversionRate?.toFixed(1)}%. Industry average is 25-40%.`,
          action: 'Review quote presentation and follow-up processes to improve conversion rates',
          impact: 'high'
        });
      } else if (salesData?.conversionRate > 50) {
        insights?.push({
          type: 'trend',
          category: 'performance',
          title: 'Excellent Conversion Rate',
          description: `Outstanding ${salesData?.conversionRate?.toFixed(1)}% quote conversion rate exceeds industry standards.`,
          action: 'Document and scale successful conversion strategies across all sales channels',
          impact: 'medium'
        });
      }
    }

    if (salesData?.overdueAmount > 0) {
      insights?.push({
        type: 'warning',
        category: 'payment',
        title: 'Outstanding Payments Alert',
        description: `$${salesData?.overdueAmount?.toLocaleString()} in overdue payments requiring attention.`,
        action: 'Implement automated payment reminders and review credit terms for overdue accounts',
        impact: 'high'
      });
    }

    if (salesData?.averagePaymentTime > 30) {
      insights?.push({
        type: 'recommendation',
        category: 'cashflow',
        title: 'Payment Terms Optimization',
        description: `Average payment time of ${salesData?.averagePaymentTime} days is above optimal range.`,
        action: 'Offer early payment discounts or adjust payment terms to improve cash flow',
        impact: 'medium'
      });
    }

    // Always include a growth opportunity insight
    insights?.push({
      type: 'opportunity',
      category: 'growth',
      title: 'Customer Engagement Opportunity',
      description: 'Analysis shows potential for increased customer engagement and upselling.',
      action: 'Focus on personalized follow-up campaigns and value-added service offerings',
      impact: 'medium'
    });

    return insights?.slice(0, 3); // Limit to 3 insights for better UX
  };

  const getStatusMessage = () => {
    switch (aiStatus) {
      case 'available':
        return 'AI-powered sales insights from real data';
      case 'quota_exceeded':
        return 'AI quota exceeded - data-driven analysis provided';
      case 'invalid_key':
        return 'API key issue detected - data-driven analysis provided';
      case 'insufficient_permissions':
        return 'API permissions issue - data-driven analysis provided';
      case 'network_error':
        return 'Network connectivity issue - data-driven analysis provided';
      case 'unavailable':
        return 'Data-driven sales analysis from actual metrics';
      case 'checking':
        return 'Analyzing real sales data...';
      default:
        return 'Sales analysis ready';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'opportunity': return 'text-success';
      case 'warning': return 'text-warning';
      case 'trend': return 'text-primary';
      case 'recommendation': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'opportunity': return 'TrendingUp';
      case 'warning': return 'AlertTriangle';
      case 'trend': return 'BarChart3';
      case 'recommendation': return 'Lightbulb';
      default: return 'Info';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional p-6">
        <div className="flex items-center justify-center h-48">
          <div className="flex items-center space-x-3">
            <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            <span className="text-muted-foreground">Analyzing sales data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional">
      {/* Enhanced API Error Alert with Diagnostic Option */}
      {showApiError && (
        <div className="p-4 border-b border-border">
          <OpenAIStatusAlert onRetry={loadSalesInsights} />
          <div className="mt-3 flex justify-end">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setDiagnosticMode(!diagnosticMode)}
            >
              <Icon name="Settings" size={14} className="mr-1" />
              {diagnosticMode ? 'Hide' : 'Show'} Diagnostic
            </Button>
          </div>
          
          {diagnosticMode && (
            <div className="mt-4">
              <AIStatusDiagnostic 
                onStatusChange={(health) => {
                  if (health === 'healthy') {
                    setShowApiError(false);
                    setAiStatus('available');
                    loadSalesInsights();
                  }
                }} 
                showHeader={false}
              />
            </div>
          )}
        </div>
      )}

      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon 
                name={aiStatus === 'available' ? "Brain" : "BarChart3"} 
                size={18} 
                className={aiStatus === 'available' ? "text-accent" : "text-primary"}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Sales Insights</h2>
              <p className="text-sm text-muted-foreground">{getStatusMessage()}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {['quota_exceeded', 'invalid_key', 'insufficient_permissions']?.includes(aiStatus) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://platform.openai.com/account/api-keys', '_blank')}
              >
                <Icon name="ExternalLink" size={14} />
                Fix API Key
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setDiagnosticMode(!diagnosticMode)}
            >
              <Icon name="Settings" size={14} />
              {diagnosticMode ? 'Hide' : 'Show'} Diagnostic
            </Button>
            <Button variant="ghost" size="sm" onClick={loadSalesInsights}>
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Enhanced Status Information */}
        {error && !showApiError && (
          <div className={`mt-4 p-3 rounded-lg ${
            ['quota_exceeded', 'invalid_key', 'insufficient_permissions', 'network_error']?.includes(aiStatus) ?
            'bg-warning/10 border border-warning/20': 'bg-muted/50 border border-muted'
          }`}>
            <div className="flex items-start space-x-2">
              <Icon 
                name={['quota_exceeded', 'invalid_key', 'insufficient_permissions', 'network_error']?.includes(aiStatus) ? 'AlertTriangle' : 'Info'} 
                size={16} 
                className={`mt-0.5 ${['quota_exceeded', 'invalid_key', 'insufficient_permissions', 'network_error']?.includes(aiStatus) ? 'text-warning' : 'text-muted-foreground'}`} 
              />
              <div className="flex-1">
                <p className={`text-sm ${
                  ['quota_exceeded', 'invalid_key', 'insufficient_permissions', 'network_error']?.includes(aiStatus) ? 'text-warning' : 'text-muted-foreground'
                }`}>
                  {error}
                </p>
                {aiStatus === 'invalid_key' && (
                  <div className="text-xs text-muted-foreground mt-2">
                    <p className="font-medium">Common solutions:</p>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                      <li>Check if your API key is correctly added to the .env file</li>
                      <li>Ensure the key hasn't expired or been revoked</li>
                      <li>Verify billing is set up in your OpenAI account</li>
                      <li>Try generating a new API key if needed</li>
                    </ul>
                  </div>
                )}
                {aiStatus === 'quota_exceeded' && (
                  <div className="text-xs text-muted-foreground mt-2">
                    <p className="font-medium">To resolve:</p>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                      <li>Add billing information to your OpenAI account</li>
                      <li>Upgrade to a higher usage tier</li>
                      <li>Wait for quota reset (usually monthly)</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Diagnostic Panel */}
      {diagnosticMode && !showApiError && (
        <div className="p-6 border-b border-border">
          <AIStatusDiagnostic 
            onStatusChange={(health) => {
              if (health === 'healthy') {
                setAiStatus('available');
                loadSalesInsights();
              }
            }} 
            showHeader={false}
          />
        </div>
      )}

      <div className="p-6 space-y-4">
        {insights?.map((insight, index) => (
          <div key={index} className="flex items-start space-x-4 p-4 bg-muted/30 rounded-lg">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon 
                name={getTypeIcon(insight?.type)} 
                size={18} 
                className={getTypeColor(insight?.type)} 
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="font-semibold text-foreground">{insight?.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(insight?.type)} bg-current/10`}>
                  {insight?.type}
                </span>
                {insight?.impact && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    insight?.impact === 'high' ? 'text-error' : 
                    insight?.impact === 'medium' ? 'text-warning' : 'text-success'
                  } bg-current/10`}>
                    {insight?.impact} impact
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  aiStatus === 'available' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
                }`}>
                  {aiStatus === 'available' ? 'AI Powered' : 'Real Data'}
                </span>
              </div>
              <p className="text-muted-foreground mb-3">{insight?.description}</p>
              {insight?.action && (
                <div className="bg-card border border-border rounded-lg p-3">
                  <h4 className="font-medium text-foreground mb-1">
                    Recommended Action:
                  </h4>
                  <p className="text-sm text-muted-foreground">{insight?.action}</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {insights?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Insights Available</h3>
            <p className="text-muted-foreground">
              {!orgId ? 'Please ensure you\'re logged into an organization to view insights.' : 'Start creating customers, quotes, and invoices to generate meaningful insights.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInsightsPanel;